import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Toast } from "primereact/toast";

import { assetService } from "../services/assetService";
import { systemService } from "../services/systemService";
import { partnerService, Partner } from "../services/partnerService";
import { transactionService } from "../services/transactionService";

import Agreement from "../model/Agreement";

import Cookies from "js-cookie";

import { checkAndReloadWithExternalBrowserParam } from "../helpers/checkAndReloadWithExternalBrowserParam";
import { normalizeAgreementLinks } from "../helpers/normalizeAgreementLinks";
import { getAppBasePath } from "../helpers/getAppBasePath";

const Appointment: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const toastRef = useRef<Toast>(null);

  const [logo, setLogo] = useState<string>();
  const [system, setSystem] = useState(
    systemService.getSystem(window.location)
  );
  const [partner, setPartner] = useState<Partner>();
  const [checkBox, setCheckBox] = useState(false);
  const [password, setPassword] = useState("");
  const [isLoadingAgreement, setIsLoadingAgreement] = useState(false);
  const [agreement, setAgreement] = useState<Agreement | null>(null);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [isConfirmingAgreement, setIsConfirmingAgreement] = useState(false);

  const appBasePath = getAppBasePath();
  const hospitalIdParam = new URLSearchParams(window.location.search).get("hospitalId");
  const urlHospitalId = hospitalIdParam && !isNaN(parseInt(hospitalIdParam, 10))
    ? parseInt(hospitalIdParam, 10)
    : undefined;
  const privacyLinkPath = urlHospitalId
    ? `${appBasePath}/privacy?hospitalId=${urlHospitalId}`
    : `${appBasePath}/privacy`;

  /** Load agreement — uses hospitalId query param if present for hospital-specific terms */
  const loadAgreement = async () => {
    setIsLoadingAgreement(true);
    try {
      const result =
        (await systemService.getAgreement(
          "UsageTermsAndConditions",
          "",
          undefined,
          urlHospitalId
        )) || null;
      setAgreement(result);
    } catch (error) {
      console.error("Load agreement failed:", error);
    } finally {
      setIsLoadingAgreement(false);
    }
  };

  /** Partner setup logic (mounted flow) */
  const onMountedPartnerUx = async () => {
    const p = await partnerService.initPartner(window.location);
    setPartner(p);

    const params = new URLSearchParams(window.location.search);
    const languageQuery =
      params.get("language") || params.get("lang") || Cookies.get("lang") || "thai";
    const language = systemService.getLanguageCode(languageQuery);
    if (language !== system.language) {
      const newSystem = { ...system, language };
      setSystem(newSystem);
      systemService.setSystem(newSystem);
      i18n.changeLanguage(language);
    }

    return false;
  };

  /** Setup UI after partner loaded */
  const onMountedPartnerUi = () => {
    if (partner) {
      setLogo(assetService.getLogo(partner.name));
      i18n.language = system.language;
    }
  };

  /** On mount */
  useEffect(() => {
    /** Handle reload with openExternalBrowser param */
    if (checkAndReloadWithExternalBrowserParam()) return;

    const init = async () => {
      await onMountedPartnerUx();
      onMountedPartnerUi();
      await loadAgreement();
    };
    init();
  }, []);

  useEffect(() => {
    if (partner) {
      onMountedPartnerUi();
    }
  }, [partner]);

  /** Handle click on Confirm in modal */
  const acceptAgreement = () => {
    setCheckBox(true);
    setIsTermsModalOpen(false);
    if (isConfirmingAgreement) {
      navigate("/call");
    }
  };

  /** Handle click on Close in modal */
  const closeAgreementModal = () => {
    setIsTermsModalOpen(false);
  };

  /** Submit logic */
  const agree = async () => {
    if (!partner?.appointmentCode) {
      toastRef.current?.show({
        severity: "warn",
        summary: t("generals.errorMessage.invalidMessage"),
      });
      return;
    }

    if (!password) {
      toastRef.current?.show({
        severity: "warn",
        summary: t("generals.errorMessage.password"),
      });
      return;
    }

    if (!checkBox) {
      toastRef.current?.show({
        severity: "warn",
        summary: t("generals.errorMessage.conditions"),
      });
      return;
    }

    const { status: responseStatus, hospitalId } =
      await transactionService.registerAppointment(
        partner.appointmentCode,
        password,
        partner.name
      );

    switch (responseStatus) {
      case 200: {
        // Load the hospital-specific agreement for final confirmation
        setIsLoadingAgreement(true);
        try {
          const updatedAgreement = await systemService.getAgreement(
            "UsageTermsAndConditions",
            partner?.name || "",
            undefined,
            hospitalId || undefined
          );
          if (updatedAgreement) {
            setAgreement(updatedAgreement);
          }
        } catch (err) {
          console.error("Failed to load hospital agreement:", err);
        } finally {
          setIsLoadingAgreement(false);
        }
        setIsConfirmingAgreement(true);
        setIsTermsModalOpen(true);
        break;
      }
      case 400:
      case 404:
        toastRef.current?.show({
          severity: "error",
          summary: t("generals.errorMessage.invalidMessage"),
        });
        break;
      case 401:
        toastRef.current?.show({
          severity: "error",
          summary: t("generals.errorMessage.invalidPassword"),
        });
        break;
      case 500:
        toastRef.current?.show({
          severity: "error",
          summary: t("generals.errorMessage.serverError"),
        });
        break;
    }
  };

  return (
    <div className="flex flex-col items-center p-4 md:p-10 gap-y-4 justify-center min-h-screen w-full">
      <Toast ref={toastRef} />
      <div className="flex flex-col items-center gap-y-4 w-full">
        <div className="w-full max-w-3xl border p-10 rounded-3xl shadow-md bg-white relative">
          {/* Logo */}
          <div className="flex justify-center items-center">
            {logo && (
              <img src={logo} alt="Logo" className="w-[172px] h-[64px]" />
            )}
          </div>

          {/* Header */}
          <div className="text-center mt-8 text-lg font-medium">
            <p>{t("pages.appointment.texts.headConditionsLoginSub")}</p>
          </div>

          {/* Messages */}
          <div className="flex flex-col text-center mt-3 space-y-2 text-lg">
            <span>{t("pages.appointment.texts.messageLine1")}</span>
            <span>{t("pages.appointment.texts.messageLine2")}</span>
            <span>{t("pages.appointment.texts.messageLine3")}</span>
          </div>

          {/* Password */}
          <div className="text-center mt-8">
            <label className="text-lg">
              {t("pages.appointment.texts.passwordLabel")}
            </label>
            <div className="mx-auto mt-2 w-9/12">
              <input
                type="password"
                className="w-full border rounded px-3 py-2"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          </div>

          {/* Terms link */}
          <div className="text-center mt-6">
            <button
              type="button"
              className="text-blue-600 underline text-md"
              onClick={() => setIsTermsModalOpen(true)}
            >
              {t("pages.appointment.texts.termsLabel")}
            </button>
          </div>

          {/* Checkbox */}
          <div className="flex justify-center mt-2 items-center">
            <input
              type="checkbox"
              id="checkbox-1"
              className="w-5 h-5 mr-2"
              checked={checkBox}
              onChange={() => setCheckBox(!checkBox)}
            />
            <label htmlFor="checkbox-1" className="text-base">
              {t("pages.appointment.texts.agreeToTermsLabel")}
            </label>
          </div>

          {/* Submit */}
          <div className="text-center mt-4">
            <button
              id="submit"
              className="min-w-[150px] bg-gradient-to-r from-[#E0BB84] via-[#E9D4B3] to-[#C19E67] text-black rounded-full py-3 px-10 text-lg font-medium shadow hover:scale-[1.02] transition"
              onClick={agree}
            >
              {t("pages.appointment.buttons.submit")}
            </button>
          </div>

          {/* Agreement Modal */}
          {isTermsModalOpen && (
            <div className="fixed inset-0 flex justify-center items-center bg-black bg-opacity-60 z-50 p-4">
              <div className="w-full max-w-2xl border p-6 rounded-3xl shadow-md bg-white relative">
                {/* Header */}
                <div className="flex flex-col gap-y-2 justify-center items-center mb-4">
                  {logo && (
                    <img src={logo} width={172} height={40} alt="svvh-logo" />
                  )}
                  <div
                    className="text-center text-lg font-medium"
                    dangerouslySetInnerHTML={{
                      __html:
                        (i18n.language === "en"
                          ? agreement?.AgreementHeaderEn
                          : agreement?.AgreementHeaderTh) ||
                        t("pages.appointment.texts.headConditions"),
                    }}
                  />
                </div>

                {/* Scrollable content */}
                <div className="relative">
                  <div className="h-[350px] overflow-y-auto border p-3 mb-4 bg-gray-100 rounded agreement-content">
                    {isLoadingAgreement ? (
                      <p className="text-center">{t("generals.loading")}</p>
                    ) : (
                      <div
                        dangerouslySetInnerHTML={{
                          __html:
                            i18n.language === "en"
                              ? normalizeAgreementLinks(agreement?.AgreementHtmlEn || "", privacyLinkPath)
                              : normalizeAgreementLinks(agreement?.AgreementHtmlTh || "", privacyLinkPath),
                        }}
                      />
                    )}
                  </div>
                </div>

                {/* Footer Buttons */}
                <div className="flex justify-end gap-2 mt-4">
                  <button
                    className="bg-gray-400 text-white px-4 py-2 rounded"
                    onClick={closeAgreementModal}
                  >
                    {t("pages.appointment.buttons.close")}
                  </button>
                  <button
                    className="bg-green-600 text-white px-4 py-2 rounded"
                    onClick={acceptAgreement}
                  >
                    {t("pages.appointment.buttons.confirm")}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Appointment;
